// The rep's and supervisor's pages (/pay…, /reps…), built for a parent route: the
// signed-in tree, and an admin's /view-as/$userId (ADR 0017's A19 amendment). One
// definition, two places, so the viewed user sees exactly their own pages. The pages read
// their params and search without the parent's types and navigate by path, keeping any
// /view-as/ prefix (shell/viewAs.ts).
import { Breadcrumb, Button, formatDate } from '@hkdevs/design-system';
import { useQuery } from '@tanstack/react-query';
import {
  createRoute,
  useLocation,
  useNavigate,
  useParams,
  type AnyRoute,
} from '@tanstack/react-router';
import { useCallback, useMemo, type ReactNode } from 'react';
import { useApiClient } from '../api/ApiProvider';
import { ApiError } from '../api/client';
import { useTenant } from '../api/queries/tenant';
import { checkQuery, pendingQuery } from '../api/queries/pay';
import { useTeamRep } from '../api/queries/team';
import { InShellError, InShellNotFound, ISO_DATE, PageGate, UUID } from '../shell/gates';
import { PageLayout } from '../shell/Page';
import { RouterLink } from '../shell/RouterLink';
import { useGo } from '../shell/viewAs';
import { payDateFromPath, stringifySearch, urlDateFromIso } from '../urlDates';
import { CheckDetailView, checkTitle } from '../views/checkDetail/CheckDetailView';
import { DownloadStatement } from '../views/checkDetail/DownloadStatement';
import { MyRepsView, parseMyRepsSearch, type MyRepsSearch } from '../views/myReps/MyRepsView';
import {
  parsePayHistorySearch,
  PayHistoryView,
  type PayHistorySearch,
} from '../views/payHistory/PayHistoryView';
import {
  parsePendingPaySearch,
  PendingPayView,
  type PendingPaySearch,
} from '../views/pendingPay/PendingPayView';
import {
  RepCheckPage,
  RepPayHistoryPage,
  RepPendingPage,
  RepSearchPage,
  type RepPayFrame,
} from '../views/repPay/RepPayPages';
import { CopySearchLink } from '../views/search/CopySearchLink';
import { ExportLineItems } from '../views/search/ExportLineItems';
import {
  LineItemSearchView,
  parseLineItemSearch,
  type LineItemSearchParams,
} from '../views/search/LineItemSearchView';

/** The page's search, through its validator (as its route validated it). */
export function useRouteSearch<T>(validate: (search: Record<string, unknown>) => T): T {
  const { search } = useLocation();
  return useMemo(() => validate(search as Record<string, unknown>), [validate, search]);
}

/** A page with no URL state (a check's detail): any search, like an old link's ?q=
 * (check pages lost their search in A16), is ignored. */
export const noSearch = (): Record<never, never> => ({});

/** The page's params, the pay date ISO (MM-DD-YYYY in the path). */
function useRouteParams(): { repId: string; payDate: string } {
  const params: { repId?: string; payDate?: string } = useParams({ strict: false });
  return { repId: params.repId ?? '', payDate: payDateFromPath(params.payDate ?? '') };
}

/** Replaces the page's search, keeping its path (and so any /view-as/ prefix). */
export function useSetSearch<T extends object>(): (next: T) => void {
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  return useCallback(
    (next: T) =>
      void navigate({
        href: `${pathname}${stringifySearch(next as Record<string, unknown>)}`,
        replace: true,
      }),
    [navigate, pathname],
  );
}

/** A supervised rep's pages, under My reps. */
function myRepsFrame(repId: string, name: string | undefined): RepPayFrame {
  return {
    page: 'reps',
    root: { label: 'My reps', href: '/reps' },
    repHref: `/reps/${repId}`,
    name,
  };
}

/** Search line items, a page header action, while the tenant has search (A16). */
function SearchLineItemsAction({ onClick }: { onClick: () => void }) {
  const { data: tenant } = useTenant();
  if (!tenant?.features.includes('line_item_search')) return null;
  return (
    <Button variant="secondary" leadingIcon="search" onClick={onClick}>
      Search line items
    </Button>
  );
}

/** The rep's own Pay History; scope and range in the URL. */
function PayHistoryPage() {
  const go = useGo();
  return (
    <PageGate page="pay">
      <PageLayout page="pay" actions={<SearchLineItemsAction onClick={() => go('/pay/search')} />}>
        <PayHistoryView
          search={useRouteSearch(parsePayHistorySearch)}
          onSearchChange={useSetSearch<PayHistorySearch>()}
          onOpenCheck={(payDate) => go(`/pay/checks/${urlDateFromIso(payDate)}`)}
        />
      </PageLayout>
    </PageGate>
  );
}

/** One of the rep's own checks, by its pay date (MM-DD-YYYY in the path, ISO inside). */
function CheckDetailPage() {
  const { payDate } = useRouteParams();
  if (!ISO_DATE.test(payDate)) return <InShellNotFound />;
  return (
    <PageGate page="pay">
      <PageLayout
        page="pay"
        title={checkTitle(payDate)}
        subtitle=""
        actions={<DownloadStatement payDate={payDate} />}
        breadcrumb={
          <Breadcrumb
            items={[
              { label: 'Pay History', href: '/pay', as: RouterLink },
              { label: formatDate(payDate) },
            ]}
          />
        }
      >
        <CheckDetailView key={payDate} payDate={payDate} />
      </PageLayout>
    </PageGate>
  );
}

/** A supervised rep's check (the breadcrumb runs through My reps and the rep). */
function RepCheckDetailPage() {
  const { repId, payDate } = useRouteParams();
  const valid = ISO_DATE.test(payDate) && UUID.test(repId);
  // The same query as the view's, so it's shared: the rep's name for the breadcrumb.
  const { data: check } = useQuery({
    ...checkQuery(useApiClient(), payDate, repId),
    enabled: valid,
  });
  if (!valid) return <InShellNotFound />;
  return (
    <PageGate page="reps">
      <RepCheckPage frame={myRepsFrame(repId, check?.rep.name)} repId={repId} payDate={payDate} />
    </PageGate>
  );
}

/** The rep's own pending pay. */
function PendingPayPage() {
  return (
    <PageGate page="payPending">
      <PageLayout page="payPending">
        <PendingPayView
          search={useRouteSearch(parsePendingPaySearch)}
          onSearchChange={useSetSearch<PendingPaySearch>()}
        />
      </PageLayout>
    </PageGate>
  );
}

/** A supervised rep's pending pay (the breadcrumb runs through My reps). */
function RepPendingPayPage() {
  const { repId } = useRouteParams();
  const search = useRouteSearch(parsePendingPaySearch);
  const setSearch = useSetSearch<PendingPaySearch>();
  const valid = UUID.test(repId);
  // The view's own query, shared: the rep's name for the title and breadcrumb.
  const { data: pending } = useQuery({
    ...pendingQuery(useApiClient(), repId),
    enabled: valid,
  });
  if (!valid) return <InShellNotFound />;
  return (
    <PageGate page="reps">
      <RepPendingPage
        frame={{ ...myRepsFrame(repId, pending?.rep.name), page: 'repsPending' }}
        repId={repId}
        search={search}
        onSearchChange={setSearch}
      />
    </PageGate>
  );
}

/** My reps: History (by rep, or every check) or Pending. */
function MyRepsPage({ view }: { view: 'history' | 'pending' }) {
  const go = useGo();
  const page = view === 'history' ? 'reps' : 'repsPending';
  return (
    <PageGate page={page}>
      <PageLayout
        page={page}
        actions={view === 'history' && <SearchLineItemsAction onClick={() => go('/reps/search')} />}
      >
        <MyRepsView
          view={view}
          search={useRouteSearch(parseMyRepsSearch)}
          onSearchChange={useSetSearch<MyRepsSearch>()}
          onOpenRep={(repId) =>
            go(view === 'history' ? `/reps/${repId}` : `/reps/${repId}/pending`)
          }
          onOpenCheck={(repId, payDate) => go(`/reps/${repId}/checks/${urlDateFromIso(payDate)}`)}
        />
      </PageLayout>
    </PageGate>
  );
}

/** A supervised rep's Pay History, under My reps. */
function RepPayHistoryRoute() {
  const { repId } = useRouteParams();
  const search = useRouteSearch(parsePayHistorySearch);
  const setSearch = useSetSearch<PayHistorySearch>();
  const go = useGo();
  const valid = UUID.test(repId);
  const rep = useTeamRep(repId, valid);
  const notFound = !valid || (rep.error instanceof ApiError && rep.error.status === 404);
  if (notFound) return <InShellNotFound />;
  return (
    <PageGate page="reps">
      <RepPayHistoryPage
        frame={myRepsFrame(repId, rep.data?.name)}
        repId={repId}
        search={search}
        onSearchChange={setSearch}
        onOpenCheck={(payDate) => go(`/reps/${repId}/checks/${urlDateFromIso(payDate)}`)}
        onOpenSearch={() => go(`/reps/${repId}/search`)}
      />
    </PageGate>
  );
}

/** Line-item search over the caller's own pay, for every role (A16), from Pay History. */
function SearchPage() {
  const search = useRouteSearch(parseLineItemSearch);
  return (
    <PageGate page="paySearch">
      <PageLayout
        page="paySearch"
        actions={
          <>
            <CopySearchLink search={search} />
            <ExportLineItems search={search} />
          </>
        }
        breadcrumb={
          <Breadcrumb
            items={[{ label: 'Pay History', href: '/pay', as: RouterLink }, { label: 'Search' }]}
          />
        }
      >
        <LineItemSearchView search={search} onSearchChange={useSetSearch<LineItemSearchParams>()} />
      </PageLayout>
    </PageGate>
  );
}

/** A supervisor's line-item search across their team, with a rep filter, from My reps. */
function TeamSearchPage() {
  const search = useRouteSearch(parseLineItemSearch);
  return (
    <PageGate page="repsSearch">
      <PageLayout
        page="repsSearch"
        actions={
          <>
            <CopySearchLink search={search} />
            <ExportLineItems supervisor search={search} />
          </>
        }
        breadcrumb={
          <Breadcrumb
            items={[{ label: 'My reps', href: '/reps', as: RouterLink }, { label: 'Search' }]}
          />
        }
      >
        <LineItemSearchView
          supervisor
          search={search}
          onSearchChange={useSetSearch<LineItemSearchParams>()}
        />
      </PageLayout>
    </PageGate>
  );
}

/** One supervised rep's line items, under My reps. */
function RepSearchRoute() {
  const { repId } = useRouteParams();
  const search = useRouteSearch(parseLineItemSearch);
  const setSearch = useSetSearch<LineItemSearchParams>();
  const valid = UUID.test(repId);
  const rep = useTeamRep(repId, valid);
  const notFound = !valid || (rep.error instanceof ApiError && rep.error.status === 404);
  if (notFound) return <InShellNotFound />;
  return (
    <PageGate page="reps">
      <PageGate page="paySearch">
        <RepSearchPage
          frame={{ ...myRepsFrame(repId, rep.data?.name), page: 'paySearch' }}
          repId={repId}
          search={search}
          onSearchChange={setSearch}
        />
      </PageGate>
    </PageGate>
  );
}

/** The rep's and supervisor's pages, as children of `parent`. */
export function payRoutes<TParent extends AnyRoute>(parent: TParent) {
  const route = <TPath extends string, TSearch extends object>(
    path: TPath,
    validateSearch: (search: Record<string, unknown>) => TSearch,
    component: () => ReactNode,
  ) =>
    createRoute({
      getParentRoute: () => parent,
      path,
      validateSearch,
      errorComponent: InShellError,
      component,
    });
  return [
    route('/pay', parsePayHistorySearch, PayHistoryPage),
    route('/pay/checks/$payDate', noSearch, CheckDetailPage),
    route('/pay/pending', parsePendingPaySearch, PendingPayPage),
    route('/pay/search', parseLineItemSearch, SearchPage),
    route('/reps', parseMyRepsSearch, function RepsHistoryPage() {
      return <MyRepsPage view="history" />;
    }),
    route('/reps/pending', parseMyRepsSearch, function RepsPendingPage() {
      return <MyRepsPage view="pending" />;
    }),
    // A static segment, so it wins over /reps/$repId (whose repId is a UUID anyway).
    route('/reps/search', parseLineItemSearch, TeamSearchPage),
    route('/reps/$repId', parsePayHistorySearch, RepPayHistoryRoute),
    route('/reps/$repId/checks/$payDate', noSearch, RepCheckDetailPage),
    route('/reps/$repId/pending', parsePendingPaySearch, RepPendingPayPage),
    route('/reps/$repId/search', parseLineItemSearch, RepSearchRoute),
  ];
}
